import {useEffect, useState, useMemo, useRef, useCallback} from "react";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {customerProductsAPI} from "../common/api/customerProductsAPI";
import {API_PRODUCTS} from "../common/api/apiConstants";
import {PRODUCT_TYPES, PRODUCT_STATUSES} from "../common/constant";
import {createURLQueryFromParams} from "../common/api/apiUtil";

export const useProducts = () => {

    const { initialized, authenticated } = useKeycloakFunctions();

    const [products, setProducts] = useState();
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [errorProducts, setErrorProducts] = useState(false);

    const shouldUpdate = useRef(true);

    useEffect(() => {
        return () => {
            shouldUpdate.current = false;
        };
    }, []);

    const getProducts = (hapimag_member_id) => {
        if (initialized && authenticated) {
            setLoadingProducts(true);
            setErrorProducts(null);

            const params = {};
            if (hapimag_member_id) {
                params.hapimag_member_id = hapimag_member_id;
            }

            customerProductsAPI.get(API_PRODUCTS + "/" + createURLQueryFromParams(params))
                .then(res => res.data)
                .then(response => {
                    if (shouldUpdate.current) {
                        setLoadingProducts(false);
                        setProducts(response);
                    }
                })
                .catch(err => {
                    if (shouldUpdate.current) {
                        setLoadingProducts(false);
                        setErrorProducts(err);
                    }
                });
        }
    };

    const getProductTranslatedType = useCallback(type => {
        const typesToTranslationKeys = {
            [PRODUCT_TYPES.SHARE]: "view.points_shares.shares.product.product_types.share",
            [PRODUCT_TYPES.ASHARE]: "view.points_shares.shares.product.product_types.a-share",
            [PRODUCT_TYPES.BSHARE]: "view.points_shares.shares.product.product_types.b-share",
            [PRODUCT_TYPES.F21]: "view.points_shares.shares.product.product_types.f21",
            [PRODUCT_TYPES.LIFETIME]: "view.points_shares.shares.product.product_types.lifetime",
            [PRODUCT_TYPES.CLASSIC]: "view.points_shares.shares.product.product_types.classic",
            [PRODUCT_TYPES.VOUCHER]: "view.points_shares.shares.product.product_types.voucher",
            [PRODUCT_TYPES.EXPLORER]: "view.points_shares.shares.product.product_types.explorer",
            [PRODUCT_TYPES.RESIDENZ_SELECT]: "view.points_shares.shares.product.product_types.residenz_select",
            [PRODUCT_TYPES.RESIDENZ]: "view.points_shares.shares.product.product_types.residenz",
            [PRODUCT_TYPES.POINTS_BASKET]: "view.points_shares.shares.product.product_types.points_basket",
            [PRODUCT_TYPES.FERIDENCE]: "view.points_shares.shares.product.product_types.feridence",
            [PRODUCT_TYPES.HOLIDAY_WORLD_VISA]: "view.points_shares.shares.product.product_types.holiday_world_visa",
            [PRODUCT_TYPES.NEUTRAL]: "view.points_shares.shares.product.product_types.neutral",
            [PRODUCT_TYPES.HOLIDAY_PASS]: "view.points_shares.shares.product.product_types.holiday_pass",
            [PRODUCT_TYPES.PRIVATE_RESIDENCE]: "view.points_shares.shares.product.product_types.private_residence",
            [PRODUCT_TYPES.INSIDER_7_PLUS]: "view.points_shares.shares.product.product_types.insider_7_plus",
            [PRODUCT_TYPES.STARTER]: "view.points_shares.shares.product.product_types.starter",
            [PRODUCT_TYPES.HAPIMAG_7_PLUS]: "view.points_shares.shares.product.product_types.hapimag_7_plus",
            [PRODUCT_TYPES.H7S]: "view.points_shares.shares.product.product_types.h7s",
            [PRODUCT_TYPES.H7M]: "view.points_shares.shares.product.product_types.h7m",
            [PRODUCT_TYPES.H7L]: "view.points_shares.shares.product.product_types.h7l",
            [PRODUCT_TYPES.FLEX_LOW_HIGH]: "view.points_shares.shares.product.product_types.flex_low_high",
            [PRODUCT_TYPES.SELECT_HIGH_4]: "view.points_shares.shares.product.product_types.select_high_4",
            [PRODUCT_TYPES.SELECT_HIGH_5]: "view.points_shares.shares.product.product_types.select_high_5",
            [PRODUCT_TYPES.MEMBERSHIP]: "view.points_shares.shares.product.product_types.membership",
            [PRODUCT_TYPES.POINTS_PRODUCT]: "view.points_shares.shares.product.product_types.points_product",
            [PRODUCT_TYPES.TRIAL_MEMBERSHIP]: "view.points_shares.shares.product.product_types.trial_membership",
            [PRODUCT_TYPES.OTHERS]: "view.points_shares.shares.product.product_types.others",
            [PRODUCT_TYPES.RESIDENCE_POINTS_ON_SUBSCRIPTION]: "view.points_shares.shares.product.product_types.residence_points_on_subscription",
            [PRODUCT_TYPES.SELECT]: "view.points_shares.shares.product.product_types.select",
            [PRODUCT_TYPES.SELECT_HIGH]: "view.points_shares.shares.product.product_types.select_high",
            [PRODUCT_TYPES.SELECT_HIGH_SHIFT]: "view.points_shares.shares.product.product_types.select_high_shift",
            [PRODUCT_TYPES.SELECT_LOW]: "view.points_shares.shares.product.product_types.select_low",
            [PRODUCT_TYPES.MEMBERSHIP_HIGH_WITH_SHARE]: "view.points_shares.shares.product.product_types.membership_high_with_share",
            [PRODUCT_TYPES.MEMBERSHIP_HIGH_WITHOUT_SHARE]: "view.points_shares.shares.product.product_types.membership_high_without_share",
            [PRODUCT_TYPES.MEMBERSHIP_LOW]: "view.points_shares.shares.product.product_types.membership_low"
        };

        return typesToTranslationKeys[type];
    }, []);

    const getProductTranslatedStatus = useCallback(status => {
        const statusesToTranslationKeys = {
            [PRODUCT_STATUSES.ACTIVE]: "view.points_shares.shares.product.product_statuses.active",
            [PRODUCT_STATUSES.CANCELLED]: "view.points_shares.shares.product.product_statuses.cancelled",
            [PRODUCT_STATUSES.PAUSED]: "view.points_shares.shares.product.product_statuses.paused",
            [PRODUCT_STATUSES.NON_CONTRIBUTORY]: "view.points_shares.shares.product.product_statuses.non_contributory",
            [PRODUCT_STATUSES.CALL_OPTION_CHANGE_A]: "view.points_shares.shares.product.product_statuses.call_option_change_a",
            [PRODUCT_STATUSES.OTHER]: "view.points_shares.shares.product.product_statuses.other",
            [PRODUCT_STATUSES.WITHOUT_RIGHT_OF_RESIDENCE]: "view.points_shares.shares.product.product_statuses.without_right_of_residence",
            [PRODUCT_STATUSES.UTILIZED]: "view.points_shares.shares.product.product_statuses.utilized"
        };

        return statusesToTranslationKeys[status];
    }, []);

    const instance = useMemo(
        () => ({
            getProductTranslatedType,
            getProductTranslatedStatus,

            getProducts,
            products,
            loadingProducts,
            errorProducts
        }),
        [
            getProducts,
            products,
            loadingProducts,
            errorProducts
        ]
    );

    return instance;
};
